'use client'

import { useTranslations } from 'next-intl'
import Section from '@/kmsApp/components/layout/section'
import { Select } from '@/kmsApp/components/forms/select'
import { Field } from '@/kmsApp/components/forms/field'
import { Textarea } from '@/kmsApp/components/forms/textarea'
import Button from '@/kmsApp/components/shared/ui/button'
import { Control, FieldErrors, UseFormRegister, UseFormSetValue, useWatch } from 'react-hook-form'
import { CourseForm as CourseFormType } from '@/kmsApp/types/forms/course-form'
import { useUser } from '@/kmsApp/providers/user-provider'
import { useMinimalPriorite } from '@/kmsApp/hooks/use-minimal-priorite'
import { useEffect, useState } from 'react'
import { ServiceCourse } from '@/kmsApp/types/models/service-course'
import { useEntrepriseContacts } from '@/kmsApp/hooks/use-entreprise-contacts'
import ContactPickerModal from '@/kmsApp/components/forms/contact-picker-modal'
import { PickedContact } from '@/kmsApp/types/forms/contact-form'
import { CustomDataValues } from '@/kmsApp/components/features/company/custom-data-values'
import { champsPersonnalisesCourse } from '@/kmsApp/components/features/course/course-custom-data-section'
import { REQUIRED } from '@/kmsApp/constants/regex-const'

interface Props {
  control: Control<CourseFormType>
  register: UseFormRegister<CourseFormType>
  setValue: UseFormSetValue<CourseFormType>
  errors: FieldErrors<CourseFormType>
  currentStep: number
  prevStep: () => void
  onNext: () => void
  disabledSelect?: boolean
  servicesList: ServiceCourse[]
}

export const TripInformations = ({
  control,
  register,
  setValue,
  errors,
  currentStep,
  prevStep,
  onNext,
  disabledSelect = false,
  servicesList,
}: Props) => {
  const t = useTranslations('web_course_form')
  const tCommon = useTranslations('common')
  const { me } = useUser()

  const [passagerPicked, setPassagerPicked] = useState<PickedContact | null>(null)

  const reservedFor = useWatch({ control, name: 'reservedFor' })
  const isReservingForExternal = reservedFor === 'external'

  // Champs que l'entreprise active veut voir renseignés sur chacune de ses courses. Leurs
  // définitions arrivent avec le compte connecté (`/account/me`), pas besoin d'un appel de plus.
  const customData = useWatch({ control, name: 'customData' })
  const champsCourse = champsPersonnalisesCourse(me?.entrepriseActive?.customDataSettings)

  const idEntrepriseActive = me?.entrepriseActive?.idEntreprise ?? null

  // Même carnet que celui proposé à l'admin (voir `useEntrepriseContacts`).
  const { carnet: collaborateurs } = useEntrepriseContacts(me?.entrepriseActive, true)

  // Pour soi : son service minimal. Pour un tiers : celui du passager choisi dans le carnet. Sans
  // service propre (ou contact saisi à la volée), c'est celui de l'entreprise active qui s'applique.
  const serviceDuPassager = isReservingForExternal
    ? passagerPicked?.kind === 'existing'
      ? collaborateurs.find((c) => c.idContact === passagerPicked.contact.id)?.minimalService
      : null
    : me?.passager?.minimalService
  const minimalPriorite = useMinimalPriorite(idEntrepriseActive, serviceDuPassager, servicesList)

  // Un service déjà choisi peut cesser d'être proposé (changement de passager ou d'entreprise).
  const idService = useWatch({ control, name: 'idService' })
  useEffect(() => {
    if (disabledSelect) return
    const choisi = servicesList.find((service) => service.id === idService)
    if (choisi && choisi.priorite < minimalPriorite) setValue('idService', 0)
  }, [disabledSelect, servicesList, idService, minimalPriorite, setValue])

  // Changer d'entreprise active invalide le passager choisi : il appartenait à l'entreprise
  // précédente. Les champs personnalisés aussi : leurs identifiants ne veulent rien dire ailleurs.
  useEffect(() => {
    setPassagerPicked(null)
    setValue('idContactPassager', null)
    setValue('customData', {})
  }, [idEntrepriseActive, setValue])

  // Le contact "existant" (idContactPassager) et le contact "créé à la volée" (passager) sont mutuellement exclusifs pour la course (soit l'un soit l'autre)
  const handlePassagerSelect = (picked: PickedContact | null) => {
    setPassagerPicked(picked)

    if (!picked) {
      setValue('idContactPassager', null)
      setValue('passager', { nom: '', prenom: '', email: '', phone: '' })
      return
    }

    // si contact est de type existant, on passe l'id contact passager
    if (picked.kind === 'existing') {
      setValue('idContactPassager', picked.contact.id, {
        shouldValidate: true,
        shouldTouch: true,
      })
      setValue('passager', { nom: '', prenom: '', email: '', phone: '' })
      // sinon pas existant -> on passe l'objet passager et pas l'id contact passager
    } else {
      setValue('idContactPassager', null)
      setValue('passager', picked.info, { shouldValidate: true, shouldTouch: true })
    }
  }

  // Quand on change de mode de réservation on réinitialise l'autre mode pour ne pas passer des données invalides
  useEffect(() => {
    if (reservedFor !== 'external') {
      setPassagerPicked(null)
      setValue('idContactPassager', null)
      setValue('passager', { nom: '', prenom: '', email: '', phone: '' })
    }
    //strange
  }, [reservedFor, setValue])

  return (
    <div className="w-full px-1">
      <Section titre={t('trip_info_title')} icon="info">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
          <Select
            control={control}
            titre={t('service')}
            placeholder={t('select_service')}
            containerClassName="col-span-2 lg:col-span-4"
            error={errors?.idService?.message}
            disabled={disabledSelect}
            description={disabledSelect ? t('imposed_by_fixed_rate') : undefined}
            options={servicesList
              // Un service imposé par un tarif fixe reste affiché même sous le minimum du passager.
              .filter((service) => disabledSelect || service.priorite >= minimalPriorite)
              .map((service) => ({
                value: service.id,
                label: service.nom,
              }))}
            rules={{
              validate: (v: number) => v !== 0 || REQUIRED(tCommon).required,
            }}
            {...register('idService', {
              required: REQUIRED(tCommon).required,
              validate: (v: number) => v !== 0 || REQUIRED(tCommon).required,
              valueAsNumber: true,
            })}
          />

          <Field
            titre={t('passengers')}
            type="number"
            icon="person"
            error={errors.nombresPassagers?.message}
            {...register('nombresPassagers', {
              validate: (v: number | string) => v !== '' || t('invalid_number'),
              pattern: {
                value: /^\d+$/,
                message: t('only_numbers'),
              },
              min: {
                value: 0,
                message: t('positive_number'),
              },
            })}
          />
          <Field
            titre={t('luggage')}
            type="number"
            icon="luggage"
            error={errors.nombresValises?.message}
            {...register('nombresValises', {
              validate: (v: number | string) => v !== '' || t('invalid_number'),
              pattern: {
                value: /^\d+$/,
                message: t('only_numbers'),
              },
              min: {
                value: 0,
                message: t('positive_number'),
              },
            })}
          />
          <Field
            titre={t('bags')}
            type="number"
            icon="shopping_bag"
            error={errors.nombresSacs?.message}
            {...register('nombresSacs', {
              validate: (v: number | string) => v !== '' || t('invalid_number'),
              pattern: {
                value: /^\d+$/,
                message: t('only_numbers'),
              },
              min: {
                value: 0,
                message: t('positive_number'),
              },
            })}
          />
          <Field
            titre={t('child_seats')}
            type="number"
            icon="child_care"
            error={errors.siegesEnfants?.message}
            {...register('siegesEnfants', {
              validate: (v: number | string) => v !== '' || t('invalid_number'),
              pattern: {
                value: /^\d+$/,
                message: t('only_numbers'),
              },
              min: {
                value: 0,
                message: t('positive_number'),
              },
            })}
          />

          {/* On demande si la course est pour le client lui-même ou s'il réserve pour quelqu'un d'autre */}
          {me && (
            <div className="flex flex-col col-span-2 lg:col-span-4">
              <p className="mb-2">{t('book_for')}</p>
              <div className="flex gap-x-6">
                <label className="flex items-center gap-x-2 cursor-pointer select-none">
                  <span className="relative flex items-center justify-center">
                    <input
                      type="radio"
                      value="me"
                      defaultChecked
                      className="peer sr-only"
                      {...register('reservedFor')}
                    />
                    <span className="h-5 w-5 rounded-full border-2 border-gray-300 transition-colors peer-checked:border-primary" />
                    <span className="absolute h-2.5 w-2.5 scale-0 rounded-full bg-primary transition-transform peer-checked:scale-100" />
                  </span>
                  {t('for_me')}
                </label>

                <label className="flex items-center gap-x-2 cursor-pointer select-none">
                  <span className="relative flex items-center justify-center">
                    <input
                      type="radio"
                      value="external"
                      className="peer sr-only"
                      {...register('reservedFor')}
                    />
                    <span className="h-5 w-5 rounded-full border-2 border-gray-300 transition-colors peer-checked:border-primary" />
                    <span className="absolute h-2.5 w-2.5 scale-0 rounded-full bg-primary transition-transform peer-checked:scale-100" />
                  </span>
                  {t('for_someone_else')}
                </label>
              </div>

              {isReservingForExternal && (
                <div className="mt-4">
                  <ContactPickerModal
                    collaborateurs={idEntrepriseActive ? collaborateurs : undefined}
                    selected={passagerPicked}
                    onSelect={handlePassagerSelect}
                    hideList={!idEntrepriseActive}
                  />
                </div>
              )}
            </div>
          )}

          <div className="col-span-2 lg:col-span-4 mt-2">
            <Textarea
              titre={t('comment')}
              placeholder={t('comment_placeholder')}
              {...register('commentaire')}
            />
          </div>

          {/* Rien à afficher pour un visiteur anonyme, ni pour une entreprise qui n'a défini
              aucun champ sur ses courses. */}
          {champsCourse.length > 0 && (
            <div className="col-span-2 lg:col-span-4">
              <CustomDataValues
                settings={champsCourse}
                values={customData ?? {}}
                onChange={(valeurs) => setValue('customData', valeurs)}
                titre={`${t('fill_in_for')} ${me?.entrepriseActive?.nom ?? t('the_company_fallback')}`}
              />
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-col md:flex-row gap-y-1.5 gap-x-2 justify-end">
          {currentStep !== 1 && (
            <Button type="button" variant="secondary" onClick={() => prevStep()}>
              {t('back')}
            </Button>
          )}
          <Button type="button" onClick={onNext} icon="arrow_forward">
            {t('configure_trip')}
          </Button>
        </div>
      </Section>
    </div>
  )
}
